import React, {
  type PropsWithChildren,
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import { config } from "../config";
import { useTranslation } from "react-i18next";
import { api, displayStatusApi } from "../helpers/api";
import { errorReporting } from "../helpers/errorReporting";
import { useDeviceId } from "./DeviceProvider";
import { Platform } from "react-native";
import { Storage } from "../helpers/storage";
import { Region } from "../types";
import { LoadingScreen } from "../screens/LoadingScreen";
import { min } from "lodash";

type HardcodedType = "device" | "config" | "playlist";

type StatusResponse =
  | {
      status: "inactive";
    }
  | {
      status: "active";
      region: Region;
      token?: string;
    };

type StatusResponseContext =
  | StatusResponse
  | {
      status: "error";
      reason: string;
      subMessages?: string[];
      showResetButton?: boolean;
    }
  | {
      status: "hard_coded";
      urlCode: string;
      region: Region;
      type?: HardcodedType;
    };

const StatusContext = createContext<
  StatusResponseContext & {
    throwError: (msg: string) => void;
    refreshStatus: () => void;
  }
>(null!);

export const StatusProvider = (props: PropsWithChildren) => {
  const { t } = useTranslation("StatusProvider");
  const deviceId = useDeviceId();

  const [status, setStatus] = useState<StatusResponseContext>();
  const statusRetryCount = useRef(0);

  const [cfRegion, setCfRegion] = useState<Region | null>(
    config.isDevelopment && config.displayStatus.devBypass ? "dev" : null,
  );

  const searchParams =
    Platform.OS === "web" ? new URLSearchParams(window.location.search) : null;
  const urlCode = searchParams?.get("code")?.replace(/\//g, "");
  const type = searchParams?.get("type")?.replace(/\//g, "") as HardcodedType;
  const paramsRegion = searchParams
    ?.get("region")
    ?.replace(/\//g, "") as Region;

  const refreshStatus = useCallback(async () => {
    if (urlCode != null && paramsRegion != null) {
      setStatus({
        status: "hard_coded",
        urlCode,
        region: paramsRegion,
        type: type ?? "config",
      });
      return;
    }

    let region = cfRegion;
    if (cfRegion == null) {
      const resp = await displayStatusApi()
        .get<{ status: "inactive" } | { status: "active"; region: string }>(
          deviceId,
        )
        .catch(({ message, code }) => {
          errorReporting.sendMessage("Failed to fetch display status", {
            message,
            code,
          });
          return null;
        });

      if (resp == null) {
        setStatus({
          status: "error",
          reason: t("Failed to get display status"),
          subMessages: [
            t("Device ID: {{deviceId}}", { deviceId }),
            t(
              "If this issue persists, please contact your Teamworks administrator.",
            ),
          ],
        });
        return;
      }

      if (resp.data.status === "inactive") {
        setStatus({ status: "inactive" });
        return;
      }

      region = resp.data.region.toLowerCase() as Region;
      setCfRegion(region);
    }

    if (region == null) {
      throw new Error("No available region");
    }

    api(region)
      .get<StatusResponse | undefined>(`/screens/${deviceId}/status`)
      .then(async (r) => {
        if (r.data?.status === "inactive") {
          setStatus({ status: "inactive" });
          return;
        }

        const storedToken = await Storage.getItem("token");
        const token = r.data?.token ?? storedToken;

        if (token != null) {
          const tokenSaved = await Storage.setItem("token", token);
          setStatus(
            tokenSaved
              ? { status: "active", token, region }
              : {
                  status: "error",
                  reason: t("Setup failed to complete"),
                  subMessages: [
                    t(
                      "Please reset your device using the button below and try again.",
                    ),
                    t(
                      "If this issue persists, please contact your Teamworks administrator.",
                    ),
                  ],
                  showResetButton: true,
                },
          );
        } else {
          setStatus({
            status: "error",
            reason: t("Screen is active but no token is present"),
            subMessages: [
              t(
                "This is usually due to a network error while setting up your device. Please reset your device using the button below and try again.",
              ),
              t(
                "If this issue persists, please contact your Teamworks administrator.",
              ),
            ],
            showResetButton: true,
          });
        }
      })
      .catch((err) => {
        errorReporting.sendError(err);
        if (status == null) {
          setStatus({ status: "error", reason: t("Network error") });
        }
      });
  }, [t, cfRegion, deviceId, paramsRegion, urlCode, type, status]);

  useEffect(() => {
    // status not set yet (first load)
    if (status == null) {
      const timeout = setTimeout(() => refreshStatus(), 100);
      return () => clearTimeout(timeout);
    }

    // status is error (failed to fetch)
    if (status.status === "error") {
      statusRetryCount.current = statusRetryCount.current + 1;
      const interval = setInterval(
        refreshStatus,
        min([2000 * statusRetryCount.current, 60000]),
      );
      return () => clearInterval(interval);
    }

    // status was returned - reset retry count
    statusRetryCount.current = 0;

    if (status.status === "inactive") {
      const interval = setInterval(
        refreshStatus,
        config.setupScreen.pollingRate,
      );
      return () => {
        clearInterval(interval);
      };
    }
  }, [status, refreshStatus]);

  if (status == null) {
    return <LoadingScreen message={t("Checking display status")} />;
  }

  return (
    <StatusContext.Provider
      {...props}
      value={{
        ...status,
        refreshStatus,
        throwError: (msg: string) => {
          setStatus({ status: "error", reason: msg });
        },
      }}
    />
  );
};

export const useStatus = () => useContext(StatusContext);

export const useSecureToken = (args?: { strict?: boolean }) => {
  const ctx = useContext(StatusContext);
  const token = ctx?.status === "active" ? ctx.token : undefined;
  if ((args?.strict ?? true) && token == null) {
    throw new Error("useSecureToken: token not ready");
  }

  return token;
};

export const useThrowError = () => {
  const ctx = useContext(StatusContext);
  if (ctx == null) {
    throw new Error("useThrowError: context not ready");
  }

  return ctx.throwError;
};

export const useRefreshStatus = () => {
  const ctx = useContext(StatusContext);
  if (ctx == null) {
    throw new Error("useRefreshStatus: context not ready");
  }

  return ctx.refreshStatus;
};
